import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Dayjs } from 'dayjs'
import {
  CSSProperties,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import SmallRemoveButtonComponent from 'components/parts/smallRemoveButton'
import { helper } from 'helper'
import { workPatternApi } from 'hooks/api/workPattern'
import AssignCountCellComponent from 'pages/shift/show/components/assignCountCell'
import HoverButtonComponent from 'pages/shift/show/components/hoverButton'
import ShiftLabelComponent from 'pages/shift/show/components/shiftLabel'
import SimpleSelectorComponent from 'pages/shift/show/components/simpleSelector'
import { FreemanShiftDirtyMap, ShiftDirtyMap } from 'pages/shift/show/type'
import { useIsHoliday } from 'store/holidaySlice'
import { Option } from 'types/option'
import { ApiShift, ShiftContent } from 'types/shift'
import { WorkPattern } from 'types/workPattern'
import { colors, Icons } from 'variables'

interface Props {
  securityFieldId: string
  days: Dayjs[]
  shift: ApiShift
  onSave: (securityIds: string[], shifts: ShiftContent[]) => void
  dirtyMap: ShiftDirtyMap
  updateDirtyMap: (
    securityId: string,
    date: Dayjs,
    value: {
      add?: string
      remove?: string
    },
  ) => void
  freemanDirtyMap: FreemanShiftDirtyMap
  updateFreemanDirtyMap: (
    originShiftIds: string[],
    lineIndex: number,
    dateIndex: number,
    value: string | null, // securityId or unselect
  ) => void
  updateFreemanShiftDirtyMap: (
    originSecurityId: string,
    lineIndex: number,
    dateIndex: number,
    value: {
      add?: string
      remove?: string
    },
  ) => void
  clearDirtyMap: () => void
}

const apiCall = {
  workPatterns: async (securityFieldId: string) => {
    const res = await workPatternApi().filterBySecurityField(securityFieldId)
    return res.data
  },
}

// workPatternId -> workPattern
type WorkPatternMap = Map<string, WorkPattern>
function createWorkPatternMap(workPatterns: WorkPattern[]): WorkPatternMap {
  return new Map(
    workPatterns.map((workPattern) => [workPattern.id, workPattern]),
  )
}

// securityId -> workPattern
type ShiftMap = Map<string, WorkPattern[][]>

function mergeShift(
  universal: Map<string, WorkPattern>,
  origin: WorkPattern[],
  add: Set<string>,
  remove: Set<string>,
): WorkPattern[] {
  // (元のシフト - 削除したシフト) + 追加したシフト
  return origin
    .filter((shift) => !remove.has(shift.id))
    .concat(
      Array.from(add)
        .map((id) => universal.get(id))
        .filter((shift): shift is WorkPattern => shift !== undefined),
    )
}

function getShift(
  shiftMap: ShiftMap,
  dirtyMap: ShiftDirtyMap,
  workPatternMap: WorkPatternMap,
  securityId: string,
  date: Dayjs,
): WorkPattern[] {
  const dateIndex = helper.day.dateIndex(date)
  const originShift = shiftMap.get(securityId)?.[dateIndex] ?? []
  const editedShift = dirtyMap.get(securityId)?.[dateIndex]
  if (editedShift == null) return originShift

  return mergeShift(
    workPatternMap,
    originShift,
    editedShift.add,
    editedShift.remove,
  )
}

function createShiftMap(
  shift: ApiShift,
  days: Dayjs[],
  workPatternMap: WorkPatternMap,
): ShiftMap {
  const orderedShifts: [string, WorkPattern[][]][] = shift.securities.map(
    (security) => {
      const workPatterns: WorkPattern[][] = Array.from(
        { length: days.length },
        () => [],
      )

      security.shifts.forEach((shift) => {
        const workPattern: WorkPattern | undefined = workPatternMap.get(
          shift.workPatternId,
        )
        if (!workPattern) return

        workPatterns[helper.day.dateIndex(shift.date)].push(workPattern)
      })
      return [security.id, workPatterns]
    },
  )

  return new Map(orderedShifts)
}

export default function EditComponent(props: Props) {
  const isHoliday = useIsHoliday()
  const [workPatterns, setWorkPatterns] = useState<WorkPattern[] | null>(null)
  const workPatternMap = useMemo<Map<string, WorkPattern>>(() => {
    if (workPatterns == null) return new Map()
    return createWorkPatternMap(workPatterns)
  }, [workPatterns])

  const fixedman = {
    getDirtyValue: (securityId: string, date: Dayjs) => {
      return (
        props.dirtyMap.get(securityId)?.[helper.day.dateIndex(date)] ?? {
          add: new Set(),
          remove: new Set(),
        }
      )
    },
    isDirty: (securityId: string, date: Dayjs) => {
      const value = fixedman.getDirtyValue(securityId, date)
      return value.add.size > 0 || value.remove.size > 0
    },
  }

  // 既存シフトのマップ
  const shiftMap: ShiftMap = useMemo(() => {
    return createShiftMap(props.shift, props.days, workPatternMap)
  }, [props.shift, props.days, workPatternMap])

  const getShiftCallback = useCallback(
    (securityId: string, date: Dayjs): WorkPattern[] => {
      return getShift(
        shiftMap,
        props.dirtyMap,
        workPatternMap,
        securityId,
        date,
      )
    },
    [shiftMap, props.dirtyMap, workPatternMap],
  )

  const shiftTable = useMemo(() => {
    return props.shift.securities.map((security) => {
      const getShiftsPerDate = (date: Dayjs) =>
        getShiftCallback(security.id, date)

      return {
        security: security,
        perDate: props.days.map((date) => {
          const shifts = getShiftsPerDate(date)
          return {
            date: date,
            isDirty: fixedman.isDirty(security.id, date),
            isLeave: shifts.length === 0,
            shifts: shifts,
          }
        }),
      }
    })
  }, [props.shift, props.days, props.dirtyMap, getShiftCallback])

  const initialFreemanShiftTable = useMemo(() => {
    if (workPatternMap == null) return []
    // 登録済みデータのみ、人ごとにグルーピングする
    return props.shift.freemen
      .filter((security) => security.shifts.length > 0)
      .map((security) => {
        const shifts: WorkPattern[][] = props.days.map(() => [])
        security.shifts.forEach((shift) => {
          const work = workPatternMap.get(shift.workPatternId)
          if (work == null) return

          shifts[helper.day.dateIndex(shift.date)].push(work)
        })

        return shifts.map((shiftsPerCell) => {
          return shiftsPerCell.length > 0
            ? {
                security,
                shiftsPerCell,
              }
            : null
        })
      })
  }, [props.shift, props.days, workPatternMap])

  const hasAnotherShift = (securityId: string, dateIndex: number): boolean => {
    return hasAnotherShiftMap.get(securityId)?.[dateIndex] ?? false
  }

  const hasAnotherShiftMap = useMemo(() => {
    const allSecurities = props.shift.securities.concat(props.shift.freemen)

    // securityId -> date -> hasAnother
    return new Map<string, boolean[]>(
      allSecurities.map((security) => {
        const another: boolean[] = Array.from(
          { length: props.days.length },
          () => false,
        )

        security.shifts.forEach((shift) => {
          const workPattern: WorkPattern | undefined = workPatternMap.get(
            shift.workPatternId,
          )

          // 警備先に紐付かない勤務パターンがあったら、別現場の勤務があると判断
          if (workPattern == null) {
            another[helper.day.dateIndex(shift.date)] = true
          }
        })

        return [security.id, another]
      }),
    )
  }, [props.shift, props.days, workPatternMap])

  const freemanShiftTable = useMemo((): {
    isDirty: boolean
    data: {
      security: {
        id: string
        name: string
      }
      shifts: WorkPattern[]
    } | null
  }[][] => {
    // key: securityId
    const freemanMap = new Map(
      props.shift.freemen.map((security) => [security.id, security]),
    )

    const maxLines = Math.max(
      initialFreemanShiftTable.length,
      props.freemanDirtyMap.length,
    )

    return [...Array(maxLines)].map((_, rowIdx) => {
      return props.days.map((_, dateIndex) => {
        const diff = props.freemanDirtyMap[rowIdx]?.[dateIndex]
        const cell = initialFreemanShiftTable[rowIdx]?.[dateIndex]

        // 差分が作られていない時
        if (diff === undefined) {
          return {
            isDirty: false,
            data: cell && {
              security: {
                id: cell.security.id,
                name: helper.formatter.nameFormat(cell.security),
              },
              shifts: cell.shiftsPerCell,
            },
          }
        }

        if (cell == null) {
          // empty -> empty
          if (diff == null) {
            return {
              isDirty: false,
              data: null,
            }
          }

          const security = freemanMap.get(diff.securityId)
          if (security == null) {
            return {
              isDirty: false,
              data: null,
            }
          }

          // empty -> selected
          return {
            isDirty: true,
            data: {
              security: {
                id: diff.securityId,
                name: helper.formatter.nameFormat(security),
              },
              shifts: mergeShift(workPatternMap, [], diff.add, diff.remove),
            },
          }
        } else {
          // selected -> empty
          if (diff == null) {
            return {
              isDirty: true,
              data: null,
            }
          }

          const security = freemanMap.get(diff.securityId)
          if (security == null) {
            return {
              isDirty: true,
              data: null,
            }
          }

          // selected -> selected
          const isChangedSecurity = cell.security.id !== diff.securityId
          const isChangedShift = diff.add.size > 0 || diff.remove.size > 0
          return {
            isDirty: isChangedSecurity || isChangedShift,
            data: {
              security: {
                id: security.id,
                name: helper.formatter.nameFormat(security),
              },
              shifts: mergeShift(
                workPatternMap,
                cell.shiftsPerCell,
                diff.add,
                diff.remove,
              ),
            },
          }
        }
      })
    })
  }, [
    initialFreemanShiftTable,
    workPatternMap,
    props.freemanDirtyMap,
    props.days,
  ])

  const calcOptionsCallback = useCallback(
    (selected: WorkPattern[], date: Dayjs) => (): Option[] => {
      if (workPatterns == null) return []
      const filteredWorkPatterns: WorkPattern[] = workPatterns.filter(
        (workPattern) => workPattern.daysOfWeeks[helper.day.dayIndex(date)],
      )

      const selectedShiftIdSet = new Set(selected.map((shift) => shift.id))
      const unselectedShifts = filteredWorkPatterns.filter((shift) => {
        return !selectedShiftIdSet.has(shift.id)
      })

      return unselectedShifts.map((shift) => ({
        value: shift.id,
        label: shift.name,
      }))
    },
    [workPatterns],
  )

  const calcFreemanOptionsCallback = useCallback(
    (dateIndex: number) => () => {
      const selectedSecurityIdSet = new Set(
        freemanShiftTable
          .map((row) => row[dateIndex].data?.security.id)
          .filter((id): id is string => id != null),
      )

      const unselectedSecurity = props.shift.freemen.filter((security) => {
        return !selectedSecurityIdSet.has(security.id)
      })

      return unselectedSecurity.map((security) => ({
        value: security.id,
        label: helper.formatter.nameFormat(security),
      }))
    },
    [props.shift.freemen, freemanShiftTable],
  )

  const assignCount = useMemo(() => {
    if (workPatterns == null) return
    return props.days.map((date, dateIndex) => {
      // workPatternId -> count
      const countMap = new Map<string, number>()
      shiftTable.forEach((row) => {
        row.perDate[dateIndex]?.shifts.forEach((shift) => {
          countMap.set(shift.id, (countMap.get(shift.id) ?? 0) + 1)
        })
      })

      freemanShiftTable.forEach((row) => {
        row[dateIndex].data?.shifts.forEach((shift) => {
          countMap.set(shift.id, (countMap.get(shift.id) ?? 0) + 1)
        })
      })

      return workPatterns.map((workPattern) => ({
        date: date,
        workPattern: workPattern,
        count: countMap.get(workPattern.id) ?? 0,
      }))
    })
  }, [shiftTable, freemanShiftTable, workPatterns])

  // 描画に必要なデータの取得
  useEffect(() => {
    apiCall.workPatterns(props.securityFieldId).then((data) => {
      setWorkPatterns(data.workPatterns)
    })
  }, [])

  const handleRemoveShift =
    (securityId: string, date: Dayjs, shift: WorkPattern) =>
    (e: MouseEvent) => {
      e.stopPropagation()

      props.updateDirtyMap(securityId, date, {
        remove: shift.id,
      })
    }

  const handleAddShift =
    (securityId: string, date: Dayjs) => (shift: Option) => {
      const currentShift = getShiftCallback(securityId, date)
      if (
        hasAnotherShift(securityId, helper.day.dateIndex(date)) &&
        currentShift.length == 0
      ) {
        alert('他の警備先でも配置されています')
      }

      props.updateDirtyMap(securityId, date, {
        add: shift.value as string,
      })
    }

  const handleRemoveFreemanSecurity =
    (lineIndex: number, dateIndex: number) => (e: MouseEvent) => {
      e.stopPropagation()

      const originShiftIds =
        initialFreemanShiftTable[lineIndex]?.[dateIndex]?.shiftsPerCell.map(
          (shift) => shift.id,
        ) ?? []
      props.updateFreemanDirtyMap(originShiftIds, lineIndex, dateIndex, null)
    }

  const handleAddFreemanSecurity =
    (lineIndex: number, dateIndex: number) => (security: Option) => {
      const securityId = security.value as string
      if (hasAnotherShift(securityId, dateIndex)) {
        alert('他の警備先でも配置されています')
      }

      const originShiftIds =
        initialFreemanShiftTable[lineIndex]?.[dateIndex]?.shiftsPerCell.map(
          (shift) => shift.id,
        ) ?? []
      props.updateFreemanDirtyMap(
        originShiftIds,
        lineIndex,
        dateIndex,
        securityId,
      )
    }

  const handleRemoveFreemanShift =
    (
      securityId: string,
      lineIndex: number,
      dateIndex: number,
      shift: WorkPattern,
    ) =>
    (e: MouseEvent) => {
      e.stopPropagation()

      props.updateFreemanShiftDirtyMap(securityId, lineIndex, dateIndex, {
        remove: shift.id,
      })
    }

  const handleAddFreemanShift =
    (securityId: string, lineIndex: number, dateIndex: number) =>
    (shift: Option) => {
      props.updateFreemanShiftDirtyMap(securityId, lineIndex, dateIndex, {
        add: shift.value as string,
      })
    }

  const handleCancel = () => {
    props.clearDirtyMap()
  }

  const handleSave = async () => {
    type ShiftRequest = {
      securityId: string
      securityFieldId: string
      workPatternId: string
      date: Dayjs
    }

    const fixedmanShifts: ShiftRequest[] = shiftTable.flatMap((row) =>
      row.perDate.flatMap((cell) =>
        cell.shifts.map((shift) => ({
          securityId: row.security.id,
          securityFieldId: props.securityFieldId,
          workPatternId: shift.id,
          date: cell.date.add(cell.date.utcOffset(), 'minutes'),
        })),
      ),
    )
    const fixedmanIds = shiftTable.map((row) => row.security.id)

    const freemanShifts: ShiftRequest[] = freemanShiftTable.flatMap((row) =>
      row.flatMap((cell, dateIndex) => {
        if (cell.data == null) return []
        const securityId = cell.data.security.id
        const date = props.days[dateIndex]
        return cell.data.shifts.map((shift) => ({
          securityId: securityId,
          securityFieldId: props.securityFieldId,
          workPatternId: shift.id,
          date: date.add(date.utcOffset(), 'minutes'),
        }))
      }),
    )

    // シフトを全削除して、表に残らなくなった隊員も取得する
    const originSecurityIds: string[] = initialFreemanShiftTable.flatMap(
      (row) =>
        row
          .map((cell) => cell?.security.id)
          .filter((id): id is string => id != null),
    )

    const freemanIds = freemanShiftTable
      .flatMap((row) =>
        row.flatMap((cell) => (cell.data ? [cell.data.security.id] : [])),
      )
      .concat(originSecurityIds)

    props.onSave(
      Array.from(new Set(fixedmanIds.concat(freemanIds))),
      Array.from(new Set(fixedmanShifts.concat(freemanShifts))),
    )
  }

  // 勤務パターンの取得が終わるまで描画しない
  if (workPatterns == null) return null
  return (
    <>
      <TableContainer sx={tableStyle}>
        <Table>
          <TableHead style={tableHeadStyle}>
            <TableRow style={headerDateStyle}>
              <TableCell style={headerCrossStyle}>{/*空白*/}</TableCell>
              {props.days.map((date) => (
                <TableCell key={`header-${date.date()}`}>
                  <Box
                    style={{
                      ...dateCellStyle,
                      ...weekColorStyle(date, isHoliday(date)),
                    }}
                  >
                    <Box>{date.format('D日')}</Box>
                    <Box>{date.format('(ddd)')}</Box>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            <TableRow style={headerCountStyle}>
              <TableCell style={headerCrossStyle}>{/*空白*/}</TableCell>
              {props.days.map((date) => (
                <TableCell key={`header-${date.date()}`}>
                  {assignCount && (
                    <AssignCountCellComponent
                      key={`count-${date.date()}`}
                      summary={assignCount?.[helper.day.dateIndex(date)]}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shiftTable.map((row) => (
              <TableRow key={row.security.id}>
                <TableCell style={headerColStyle}>
                  <Typography style={nameCellStyle}>
                    {helper.formatter.nameFormat(row.security)}
                  </Typography>
                </TableCell>
                {row.perDate.map((cell) => (
                  <TableCell key={`row-${row.security.id}-${cell.date.date()}`}>
                    {cell.isDirty && <Box style={editedCellStyle} />}
                    <Box style={shiftCellContainerStyle}>
                      {cell.shifts.map((shift) => (
                        <HoverButtonComponent
                          key={`cell-${row.security.id}-${cell.date.date()}-${shift.id}`}
                          button={
                            <SmallRemoveButtonComponent
                              onClick={handleRemoveShift(
                                row.security.id,
                                cell.date,
                                shift,
                              )}
                              style={unselectButtonStyle}
                            />
                          }
                        >
                          <ShiftLabelComponent style={shiftCellStyle}>
                            <Box style={cellTextStyle}>{shift.name}</Box>
                          </ShiftLabelComponent>
                        </HoverButtonComponent>
                      ))}
                      <SimpleSelectorComponent
                        options={calcOptionsCallback(cell.shifts, cell.date)}
                        onSelect={handleAddShift(row.security.id, cell.date)}
                        emptyText="選択できるシフトがありません"
                      >
                        {cell.isLeave ? (
                          <ShiftLabelComponent style={leaveShiftCellStyle}>
                            休み
                          </ShiftLabelComponent>
                        ) : (
                          <ShiftLabelComponent style={shiftCellStyle}>
                            <Icons.addButton style={shiftCellIconStyle} />
                          </ShiftLabelComponent>
                        )}
                      </SimpleSelectorComponent>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {freemanShiftTable.map((row, rowIdx) => (
              <TableRow key={`free-${rowIdx}`}>
                <TableCell style={headerColStyle}>
                  <Box style={freeLabelStyle}>free</Box>
                </TableCell>
                {row.map((cell, dateIdx) => (
                  <TableCell key={`free-${rowIdx}-${dateIdx}`}>
                    {cell.isDirty && <Box style={editedCellStyle} />}
                    <Box style={shiftCellContainerStyle}>
                      {cell.data != null ? (
                        <>
                          {cell.data.shifts.map((shift) => (
                            <HoverButtonComponent
                              key={`free-${rowIdx}-${dateIdx}-${shift.id}`}
                              button={
                                <SmallRemoveButtonComponent
                                  onClick={handleRemoveFreemanShift(
                                    cell.data!.security.id,
                                    rowIdx,
                                    dateIdx,
                                    shift,
                                  )}
                                  style={unselectButtonStyle}
                                />
                              }
                            >
                              <ShiftLabelComponent style={shiftCellStyle}>
                                <Box style={cellTextStyle}>{shift.name}</Box>
                              </ShiftLabelComponent>
                            </HoverButtonComponent>
                          ))}
                          <SimpleSelectorComponent
                            options={calcOptionsCallback(
                              cell.data.shifts,
                              props.days[dateIdx],
                            )}
                            onSelect={handleAddFreemanShift(
                              cell.data.security.id,
                              rowIdx,
                              dateIdx,
                            )}
                            emptyText="選択できるシフトがありません"
                          >
                            <ShiftLabelComponent style={shiftCellStyle}>
                              <Icons.addButton style={shiftCellIconStyle} />
                            </ShiftLabelComponent>
                          </SimpleSelectorComponent>
                          <HoverButtonComponent
                            button={
                              <SmallRemoveButtonComponent
                                onClick={handleRemoveFreemanSecurity(
                                  rowIdx,
                                  dateIdx,
                                )}
                                style={unselectButtonStyle}
                              />
                            }
                          >
                            <ShiftLabelComponent style={shiftCellStyle}>
                              <Box style={cellTextStyle}>
                                {cell.data.security.name}
                              </Box>
                            </ShiftLabelComponent>
                          </HoverButtonComponent>
                        </>
                      ) : (
                        <SimpleSelectorComponent
                          options={calcFreemanOptionsCallback(dateIdx)}
                          onSelect={handleAddFreemanSecurity(rowIdx, dateIdx)}
                          emptyText="選択できる隊員がいません"
                        >
                          <ShiftLabelComponent style={shiftCellStyle}>
                            <Icons.security style={shiftCellIconStyle} />
                          </ShiftLabelComponent>
                        </SimpleSelectorComponent>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={actionStyle}>
        <Button onClick={handleCancel} variant="negative">
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained">
          保存
        </Button>
      </Box>
    </>
  )
}

const tableBorder = '1px solid #B4BEBE'
const tableStyle: SxProps = {
  overflow: 'scroll',
  maxHeight: 'calc(100vh - 300px)',
  '& table': {
    borderCollapse: 'separate',
    height: '100%',
  },

  '& td, th': {
    position: 'relative',
    height: '100%',
    border: 'none',
  },

  '& th': {
    padding: '0',
    borderBottom: tableBorder,
  },

  '& tr:first-of-type th': {
    borderTop: tableBorder,
  },

  '& tr:not(:first-of-type) td': {
    borderTop: tableBorder,
  },

  '& :is(td, th):not(:last-of-type)': {
    borderRight: tableBorder,
  },
}

const headerCrossStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  left: 0,
  backgroundColor: 'inherit',
  zIndex: 3,
}

const tableHeadStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  borderTop: tableBorder,
}

const headerDateStyle: CSSProperties = {
  backgroundColor: '#F7F8F8',
}

const headerCountStyle: CSSProperties = {
  backgroundColor: 'white',
}

const headerColStyle: CSSProperties = {
  position: 'sticky',
  left: 0,
  backgroundColor: 'white',
  zIndex: 1,
}

const nameCellStyle: CSSProperties = {
  fontSize: '16px',
  whiteSpace: 'nowrap',
  width: 130,
  paddingLeft: '8px',
}

const dateCellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  alignItems: 'center',
  lineHeight: '1em',
  padding: '8px',
}

const shiftCellContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}

const shiftCellStyle: CSSProperties = {
  border: tableBorder,
}

const leaveShiftCellStyle: CSSProperties = {
  ...shiftCellStyle,
  backgroundColor: '#AFB6B6',
  color: 'white',
  fontWeight: 'bold',
}

const unselectButtonStyle: CSSProperties = {
  transform: 'translate(-5px, -5px)',
}

const shiftCellIconStyle: CSSProperties = {
  fontSize: '14px',
}

const editedCellStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  border: '2px solid red',
}

const freeLabelStyle: CSSProperties = {
  padding: '0 16px',
  width: 'fit-content',
  fontSize: '16px',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '4px',
  backgroundColor: '#80D3FF',
  marginLeft: '8px',
}

const actionStyle: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
  marginBottom: '8px',
  gap: '16px',
  '& > button': {
    padding: '4px 16px',
  },
}

const cellTextStyle: CSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}

const weekColorStyle = (date: Dayjs, isHoliday: boolean): CSSProperties => {
  if (date.day() === 0 || isHoliday) {
    return {
      // 日曜日
      color: colors.WEEK.SUN_COLOR,
      backgroundColor: colors.WEEK.SUN_BG_COLOR,
    }
  }

  if (date.day() === 6) {
    return {
      // 土曜日
      color: colors.WEEK.SAT_COLOR,
      backgroundColor: colors.WEEK.SAT_BG_COLOR,
    }
  }

  return {
    // 平日 (色の上書き無し)
  }
}
